<script setup>

import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import HeaderTitle from "@/components/layout/HeaderTitle";


// Services
import CollaboratorService from '@/api/services/collaborator.service';
import EmployeeService from '@/api/services/employee.service';
import ProviderService from '@/api/services/provider.service';
import Notification from '@/utils/plugins/NotificationService';
import Common from '@/api/services/common.service';

import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";
import EmployeeAvatar from "@/components/shared/EmployeeAvatar.vue";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

const authStore = useAuthStore();
const uiStore = useUiStore();
const router = useRouter();
const collaboratorsFiltersStorageKey = 'collaborators-filters';
const collaboratorsRestoreFiltersStorageKey = 'collaborators-restore-filters';

const collaborators = ref([]);
const filteredCollaborators = ref([]);
const remote = ref(null);
const providers = ref([]);
const provider = ref(null);
const job = ref(null);
const collaboratorName = ref('');
const params = ref({
  employeeType: "EXTERNAL_COLLABORATOR",
  active: true,
});
const showModalUnsubscribe = ref(false);
const deleteDialogShown = ref(false);
const selectedColaborator = ref(null);
const dateLeave = ref(null);
const page = ref(1);
const itemsPerPage = ref(10);
const totalResults = ref(0);
// Referencias a los selectores excluyentes entre sí
// const excluyentsDropDownsRefs = ref(['departmentsSharedDropDown', 'jobsSharedDropDown', 'activeSharedDropDown', 'remoteSharedDropDown']);

const user = computed(() => {
  return authStore.getUserLogged;
});

onMounted(() => {
  if (user.value.roles.includes('ROLE_ADMIN')) {
    applyCollaboratorsFiltersState();
    params.value['order[fullName]'] = 'ASC';
    getCollaborators();
    // getTempData();
    getProviders();
  }
})


const search = () => {
  page.value = 1;
  storeFilters();
  getCollaborators();
}

const storeFilters = () => {
  sessionStorage.setItem(collaboratorsFiltersStorageKey, JSON.stringify({
    collaboratorName: collaboratorName.value,
    provider: provider.value,
    page: page.value,
  }));
}

const applyStoredFilters = () => {
  const storedFilters = sessionStorage.getItem(collaboratorsFiltersStorageKey);

  if (!storedFilters) {
    return;
  }

  const parsedFilters = JSON.parse(storedFilters);

  collaboratorName.value = parsedFilters.collaboratorName || '';
  provider.value = parsedFilters.provider ?? null;
  page.value = parsedFilters.page || 1;
}

const clearStoredFilters = () => {
  sessionStorage.removeItem(collaboratorsFiltersStorageKey);
}

const applyCollaboratorsFiltersState = () => {
  const shouldRestoreFilters = sessionStorage.getItem(collaboratorsRestoreFiltersStorageKey) === 'true';

  if (shouldRestoreFilters) {
    applyStoredFilters();
    sessionStorage.removeItem(collaboratorsRestoreFiltersStorageKey);
    return;
  }

  clearStoredFilters();
}

const handlePageChange = (newPage) => {
  page.value = newPage;
  storeFilters();
  getCollaborators();
}

const filterCollaborators = () => {
  if (collaboratorName.value && collaboratorName.value.length > 0) {
    params.value.orSearch_fullName = collaboratorName.value.toLowerCase()
  } else {
    delete params.value['orSearch_fullName'];
  }
  if (provider.value) {
    params.value['provider.id'] = provider.value
  } else {
    delete params.value['provider.id'];
  }
  if (job.value) {
    params.value['job.name'] = job.value
  } else {
    delete params.value['job.name'];
  }
  // if (remote.value !== null && remote.value != 3) {
  if (remote.value) {
    params.value.remote = remote.value
  } else {
    delete params.value['remote']
  }
}

const getCollaborators = () => {
  filterCollaborators();
  params.value.page = page.value;
  params.value.itemsPerPage = itemsPerPage.value;
  // uiStore.setLoading(true);
  uiStore.startLoading();
  EmployeeService.getEmployees(params.value)
    .then((res) => {
      collaborators.value = res.data['hydra:member'];
      filteredCollaborators.value = collaborators.value;
      totalResults.value = res.data['hydra:totalItems'];
      // uiStore.setLoading(false);
      uiStore.stopLoading();
    })
    .catch(() => {
      Notification.addNotification('Error al intentar eliminar el empleado seleccionado.', 'error', 6000);
      // uiStore.setLoading(false);
      uiStore.stopLoading();
    })
}

const getProviders = (searchQuery = '') => {
  //const params = {query: {name: searchQuery}};
  const params = (searchQuery.length > 0 ? { name: searchQuery } : {});

  ProviderService.getProviders(params).then((res) => {
    providers.value = res.data['hydra:member'];
  })
}

const openCollaborator = (collaborator) => {
  router.push(`/collaborators/${collaborator.id ? collaborator.id : 'new'}`)
}
const deleteCollaborator = (collaborator) => {
  // uiStore.setLoading(true);
  uiStore.startLoading();
  CollaboratorService.deleteCollaborator(collaborator.id)
    .then(() => {
      Notification.addNotification('Empleado eliminado correctamente.', 'success', 6000);
      // uiStore.setLoading(false);
      uiStore.stopLoading();
      selectedColaborator.value = null;
      deleteDialogShown.value = false;
      getCollaborators();
    })
    .catch(() => {
      Notification.addNotification('Error al intentar eliminar el empleado seleccionado.', 'error', 6000);
      // uiStore.setLoading(false);
      uiStore.stopLoading();
      selectedColaborator.value = null;
      deleteDialogShown.value = false;
      getCollaborators();
    })
}
const unsubscribeColaborator = async (collaborator) => {
  if (dateLeave.value) {
    // uiStore.setLoading(true);
    uiStore.startLoading();
    collaborator.active = false;
    collaborator.dateLeave = dateLeave;
    if (collaborator.job?.['@id']) {
      collaborator.job = collaborator.job['@id'];
    } else {
      delete collaborator.job;
    }
    if (collaborator.provider?.['@id']) {
      collaborator.provider = collaborator.provider['@id'];
    } else {
      delete collaborator.provider;
    }
    await EmployeeService.updateEmployee(collaborator, collaborator.id)
      .then(() => {
        Notification.addNotification('Colaborador dado de baja correctamente', 'success', 6000);
        // uiStore.setLoading(false);
        uiStore.stopLoading();
        selectedColaborator.value = null;
        dateLeave.value = null;
        getCollaborators();
      })
      .catch(() => {
        Notification.addNotification('Error al dar de baja al empleado', 'error', 6000);
        // uiStore.setLoading(false);
        uiStore.stopLoading();
        selectedColaborator.value = null;
        dateLeave.value = null;
        getCollaborators();
      });
  }
}

const closeModalUnsubscribe = () => {
  showModalUnsubscribe.value = false;
}


/* Evento que se ejecuta cuando un selector se despliega */

const ifIsInArray = (userRoles, role) => {
  return Common.ifIsInArray(userRoles, role);
}

</script>

<template>
  <div v-if="user && ifIsInArray(user.roles, 'ROLE_ADMIN')" id="collaborators">
    <HeaderTitle title="Producción" />
    <div class="d-flex flex-row justify-content-start">
      <img src="@/assets/icons/search-blue.svg" width="30" alt="">
      <div class="title-text ml-4">
        Colaboradores
      </div>
    </div>
    <!--Formulario Busqueda  -->
    <el-row class="mt-2">
      <el-card class="card-search card-abs w-100 wback" body-style="padding:0">
        <div class="search-form">
          <div class="row">
            <div class="col-md-4 col-12">
              <div class="form-item">
                <label>Nombre</label>
                <el-input v-model="collaboratorName" placeholder="Buscar por nombre" clearable @change="search()">
                </el-input>
              </div>
            </div>
            <div class="col-md-4 col-12">
              <div class="form-item">
                <label class="w-100">Proveedor</label>
                <el-select v-model="provider" placeholder="Todos" filterable clearable @change="search()">
                  <el-option v-for="item in providers" :key="item.id" :label="item.legalName" :value="item.id">
                  </el-option>
                </el-select>
              </div>
            </div>
            <div class="col-md-4 col-12">
              <div class="d-flex justify-content-center collaborator-search-actions">
                <div class="col-md-6 align-self-center ">
                  <button type="submit" class="btn btn_red btn__new" @click="openCollaborator({})">
                    Nuevo
                  </button>
                </div>
                <div class="col-md-6 align-self-center ">
                  <button type="submit" @click="search()" class="btn btn_yellow btn__search">
                    BUSCAR
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </el-card>
    </el-row>
    <!--Tabla colaboradores  -->
    <div class="table table-users table-not-mobile">
      <div v-if="collaborators && collaborators.length > 0">
        <table>
          <thead>
            <tr class="justify-content-start gap-2">
              <th>
                <img src="@/assets/icons/user.svg" width="20" height="20" alt="name-icon" />
                NOMBRE
              </th>
              <th>
                <img src="@/assets/icons/email-icon.svg" width="20" height="20" alt="email-icon" />
                CORREO EXTERNO
              </th>
              <th>
                <img src="@/assets/icons/mouse-icon.svg" width="20" height="20" alt="department-icon" />
                HORARIO ESPAÑA
              </th>
              <th>
                <img src="@/assets/icons/medal-icon.svg" width="20" height="20" alt="position-icon" />
                PAIS UBICACION
              </th>
              <th>
                <img src="@/assets/icons/medal-icon.svg" width="20" height="20" alt="position-icon" />
                PROVEEDOR
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="collaborator in filteredCollaborators" :key="collaborator.id">
              <td>
                <div class="row-table-image-name-link">
                  <EmployeeAvatar :employee="collaborator" />

                  <div class="link-name col-7">
                    <router-link :to="{ name: 'collaborator', params: { id: collaborator.id } }"
                      style="text-decoration: none">
                      {{ collaborator.name }} {{ collaborator.lastName }}
                    </router-link>
                  </div>
                </div>
              </td>
              <td>
                {{ collaborator.email }}
              </td>
              <td>
                <el-checkbox disabled v-model="collaborator.timeZoneSpain" />
              </td>
              <td>
                {{ collaborator.countryLocation }}
              </td>
              <td>
                {{ collaborator.provider ? collaborator.provider.legalName : 'No' }}
              </td>
              <td>
                <div class="buttons d-inline-flex">
                  <div class="pointer">
                    <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Editar"
                      @click="openCollaborator(collaborator)">
                      <font-awesome-icon icon="circle" style="color: #f7d20e" />
                      <font-awesome-icon icon="plus" class="fa-inverse" transform="shrink-6" />
                    </font-awesome-layers>
                  </div>
                  <div class="pointer">
                    <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Dar de baja"
                      @click="showModalUnsubscribe = true; selectedColaborator = collaborator;">
                      <font-awesome-icon icon="circle" style="color:red" />
                      <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                    </font-awesome-layers>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <h5>No se han encontrado empleados según los filtros especificados...</h5>
      </div>
      <SharedElementPagination class="pagination" v-model:current-page="page" :total="totalResults"
        v-model:page-size="itemsPerPage" style="padding: 0px 0px 20px 0px;" @change="handlePageChange" show-always />
    </div>
    <div class="table-mobile">
      <div v-if="collaborators && collaborators.length > 0">
        <el-row v-for="collaborator in filteredCollaborators" :key="collaborator.id" class="mt-3">
          <el-card class="collaborator-mobile-card w-100 pointer" @click="openCollaborator(collaborator)">
            <div class="collaborator-mobile-card__delete">
              <font-awesome-layers class="fa-2x pointer" title="Dar de baja"
                @click.stop="showModalUnsubscribe = true; selectedColaborator = collaborator;">
                <font-awesome-icon icon="circle" style="color:red" />
                <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
              </font-awesome-layers>
            </div>
            <div class="collaborator-mobile-card__header">
              <div class="collaborator-mobile-card__identity">
                <EmployeeAvatar :employee="collaborator" />
                <div class="collaborator-mobile-card__title-wrap">
                  <router-link class="collaborator-mobile-card__title" @click.stop
                    :to="{ name: 'collaborator', params: { id: collaborator.id } }">
                    {{ collaborator.name }} {{ collaborator.lastName }}
                  </router-link>
                </div>
              </div>
            </div>
            <div class="collaborator-mobile-card__item">
              <span><b class="collaborator-mobile-card__label">Correo externo:</b>
                {{ collaborator.email || '--' }}</span>
            </div>
            <div class="collaborator-mobile-card__item">
              <span><b class="collaborator-mobile-card__label">Horario España:</b>
                {{ collaborator.timeZoneSpain ? 'Si' : 'No' }}</span>
            </div>
            <div class="collaborator-mobile-card__item">
              <span><b class="collaborator-mobile-card__label">Pais ubicacion:</b>
                {{ collaborator.countryLocation || '--' }}</span>
            </div>
            <div class="collaborator-mobile-card__item">
              <span><b class="collaborator-mobile-card__label">Proveedor:</b>
                {{ collaborator.provider ? collaborator.provider.legalName : 'No' }}</span>
            </div>
          </el-card>
        </el-row>
      </div>
      <div v-else>
        <h5>No se han encontrado empleados segÃºn los filtros especificados...</h5>
      </div>
      <SharedElementPagination class="pagination" v-model:current-page="page" :total="totalResults"
        v-model:page-size="itemsPerPage" style="padding: 0px 0px 20px 0px;" @change="handlePageChange" show-always />
    </div>

    <!--Eliminar colaborador-->
    <el-dialog v-model="deleteDialogShown" :title="'¿Desea eliminar/dar de baja permanentemente este empleado?'"
      :show-close="false" @confirm="deleteCollaborator(selectedColaborator)"
      @close="selectedColaborator = null; deleteDialogShown = false;"
      @decline="selectedColaborator = null; deleteDialogShown = false;" />
    <el-dialog v-model="showModalUnsubscribe" width="500" :show-close="false" :lock-scroll="true"
      class="unsubscribe-dialog" style="border-radius: 20px;" @close="closeModalUnsubscribe()">
      <div class="unsubscribe-dialog__content" v-if="selectedColaborator">
        <div class="unsubscribe-dialog__header">
          <div class="title-text unsubscribe-dialog__title">
            Dar de baja colaborador
          </div>
          <img class="unsubscribe-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt=""
            @click="closeModalUnsubscribe()">
        </div>

        <p class="unsubscribe-dialog__message">
          Seleccione la fecha de baja en la empresa para el colaborador:
        </p>
        <p class="unsubscribe-dialog__employee">
          {{ selectedColaborator.name }} {{ selectedColaborator.lastName }}
        </p>

        <div class="row item unsubscribe-dialog__field">
          <div class="form-item date-picker">
            <label for="dateLeave" class="w-100">Fecha de baja</label>
            <el-date-picker v-model="dateLeave" type="date" placeholder="Seleccione la fecha de baja"
              style="width: 100%" :clearable="true" :picker-options="{ firstDayOfWeek: 1 }" />
          </div>
        </div>

        <div class="unsubscribe-dialog__actions">
          <div class="unsubscribe-dialog__action">
            <button class="btn btn_yellow unsubscribe-dialog__button" @click="closeModalUnsubscribe()">
              Cancelar
            </button>
          </div>
          <div class="unsubscribe-dialog__action">
            <button class="btn btn_red unsubscribe-dialog__button" :disabled="!dateLeave"
              @click="unsubscribeColaborator(selectedColaborator); closeModalUnsubscribe()">
              Aceptar
            </button>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.card-search {
  height: auto;
  border-radius: 15px;
  max-height: 480px;
  background: white url("../../assets/images/rrhh_empleados_coffe.png") no-repeat;
  background-position-x: 100%;
  overflow: visible !important;
}

#collaborators {
  //text-align: left;

  margin-bottom: 20px;

  .label-collaborators {
    color: #434343;
    float: left;

  }


  label.checkbox-label input[type=checkbox] {
    position: relative;
    vertical-align: middle;
    bottom: 1px;
  }

}

.subtitle {
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  .subtitle__search-icon {
    fill: $bg-blue;
    width: 45px;
    margin-right: 10px;
  }

  .subtitle__search-text {
    color: $bg-blue;
    letter-spacing: -0.05em;
    font-size: 2.5em;
    font-weight: bolder;
  }
}

.btn__search {
  color: $bg-blue;
  text-transform: uppercase;
  font-weight: bolder;
  margin-left: 10px;

}

.btn__search,
.btn__new {
  height: 3em;
  margin-top: 22px;
}


.icon__table {
  fill: $bg-blue;
  width: 2em;
}

.table-users {
  table {
    tbody {
      td {
        .link-name {
          margin: auto;
          margin-left: 10px;
        }
      }
    }
  }
}

.img-thumbnail {
  width: 50px;
}

.img-modal {
  width: 325px;
}

.modal-image {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.unsubscribe-dialog__content {
  padding-bottom: 8px;
}

.unsubscribe-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-top: 2px;
}

.unsubscribe-dialog__title {
  color: $font-blue-color;
  line-height: 1.15;
}

.unsubscribe-dialog__close {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.unsubscribe-dialog__message {
  margin: 18px 0 6px;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.35;
}

.unsubscribe-dialog__employee {
  margin-bottom: 18px;
  color: #6b7280;
  font-size: 17px;
  font-weight: 600;
}

.unsubscribe-dialog__field {
  margin: 0;
}

.unsubscribe-dialog__actions {
  display: flex;
  gap: 20px;
  margin-top: 24px;
  width: 100%;
}

.unsubscribe-dialog__action {
  flex: 1 1 0;
  min-width: 0;
  margin-right: 4px;
}

.unsubscribe-dialog__button {
  width: 100%;
}

.table-mobile {
  display: none;
}

.collaborator-mobile-card {
  border-radius: 18px;
}

.collaborator-mobile-card :deep(.el-card__body) {
  position: relative;
  padding: 18px 18px 20px;
}

.collaborator-mobile-card__delete {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 1;
}

.collaborator-mobile-card__header {
  margin-bottom: 14px;
}

.collaborator-mobile-card__identity {
  display: flex;
  align-items: center;
  gap: 12px;
}

.collaborator-mobile-card__identity :deep(img) {
  width: 52px;
  height: 52px;
  min-width: 52px;
  min-height: 52px;
}

.collaborator-mobile-card__title-wrap {
  min-width: 0;
}

.collaborator-mobile-card__title {
  color: $bg-blue;
  font-size: 18px;
  font-weight: 800;
  padding-right: 42px;
  text-decoration: none;
  word-break: break-word;
}

.collaborator-mobile-card__item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 10px;
  word-break: break-word;
}

.collaborator-mobile-card__label {
  color: $bg-blue;
  font-weight: 800;
}

.collaborator-mobile-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
}

@media (min-width: 768px) {
  .table-mobile {
    display: none;
  }
}

@media (max-width: 767px) {
  .card-search {
    background-size: 92px;
    background-position: calc(100% + 16px) 12px;
    padding-right: 14px;
  }

  .collaborator-search-actions {
    flex-direction: column;
    align-items: stretch !important;
    padding-right: 14px;
  }

  .collaborator-search-actions .col-md-6 {
    width: 100%;
    max-width: 100%;
    padding-right: 0;
    padding-left: 0;
  }

  .collaborator-search-actions .btn__new,
  .collaborator-search-actions .btn__search {
    width: 100%;
    margin-left: 0;
  }

  .table-not-mobile {
    display: none;
  }

  .table-mobile {
    display: block;
    padding-right: 0;
    padding-left: 0;
  }

  .unsubscribe-dialog__title {
    font-size: 26px;
  }

}
</style>
